<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown me-2" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" class="icon-button">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaImage :src="currentLanguageFlag" class="w-9 h-9 flag-image rounded-full border-2 border-black" />
          </span>
        </VaButton>
      </template>

      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList>
          <VaListItem
            v-for="item in languages"
            :key="item.code"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="setLanguage(item)"
          >
            <VaImage :src="getFlagUrl(item.code)" class="w-10 h-6 mr-2" />
            {{ item.name }}
          </VaListItem>
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useColors } from "vuestic-ui"
import { useUserStore } from "@/stores/user-store"
import { storeToRefs } from "pinia"
import { usePeople } from "@/pages/common/composables/usePeople"
import { warningMessage } from "@protein/core-vue3/helpers/message"

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))
const { locale } = useI18n()
const isShown = ref(false)
const config = inject("$config") as any
const languages = config?.languages || []
const userStore = useUserStore()
const { saveCulture } = usePeople()

const { userLanguage } = storeToRefs(userStore)

const logos = import.meta.glob("@/assets/flags/*.svg", { eager: true, import: "default" })
// Dynamic import function for flags
const getFlagUrl = (languageCode: string): string => {
  const path = `/src/assets/flags/${languageCode}.svg`
  const src = (logos[path] as string | undefined) ?? (logos["/src/assets/flags/en-US.png"] as string | undefined) ?? ""
  return typeof src === "string" ? src : ""
}

const currentLanguageFlag = computed(() => {
  return getFlagUrl(userLanguage.value || "en-US")
})

const setLanguage = (item: { code: string }) => {
  return {
    onClick: () => {
      userStore.setUserLanguage(item.code)
      locale.value = item.code
      if (userStore.isRegisteredInJtp) {
        saveCulture(item.code)
      } else {
        warningMessage("general.messages.notRegisteredSessionOnly")
      }
    },
  }
}

onMounted(() => {
  if (userStore.user?.language) {
    locale.value = userStore.user.language
  }
})
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }
}

.icon-button span {
  padding: 0px !important;
}

.flag-image img {
  border-radius: 9999px;
}
</style>
